import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  getDepositSettings,
  updateDepositSettings,
  createDepositSettings,
  toggleDepositStatus,
  getDepositSettingsById,
  deleteDepositSettings
} from '@/api/online-ordering/settings/deposit-settings'


export const fetchDepositSettings = createAsyncThunk(
  'depositSettings/fetchAll',
  async (restaurantId, { rejectWithValue }) => {
    const response = await getDepositSettings(restaurantId)
    if (response.status === 'success') return response.data
    return rejectWithValue(response.message)
  }
)

export const createNewDepositSetting = createAsyncThunk(
  'depositSettings/create',
  async (data, { rejectWithValue }) => {
    const response = await createDepositSettings(data)
    if (response.status === 'success')
      return response.data.reservation_deposit_rule
    return rejectWithValue(response.message)
  }
)

export const fetchDepositSettingById = createAsyncThunk(
  'depositSettings/fetchOne',
  async (id, { rejectWithValue }) => {
    const response = await getDepositSettingsById(id)
    if (response.status === 'success')
      return response.data.reservation_deposit_rule
    return rejectWithValue(response.message)
  }
)

export const updateExistingDepositSetting = createAsyncThunk(
  'depositSettings/update',
  async ({ id, data }, { rejectWithValue }) => {
    const response = await updateDepositSettings(id, data)
    if (response.status === 'success')
      return response.data.reservation_deposit_rule
    return rejectWithValue(response.message)
  }
)

export const toggleDepositSettingStatus = createAsyncThunk(
  'depositSettings/toggleStatus',
  async ({ id, data }, { rejectWithValue }) => {
    const response = await toggleDepositStatus(id, data)
    if (response.status === 'success')
      return response.data.reservation_deposit_setting
    return rejectWithValue(response.message)
  }
)

export const deleteDepositSettingById = createAsyncThunk(
  'depositSettings/delete',
  async (id, { rejectWithValue }) => {
    const response = await deleteDepositSettings(id)
    if (response.status === 'success') return id
    return rejectWithValue(response.message)
  }
)

const depositSettingsSlice = createSlice({
  name: 'depositSettings',
  initialState: {
    depositSettings: {},
    depositRules: [],
    singleDepositRule: {},
    getLoading: false,
    createLoading: false,
    updateLoading: false,
    deleteLoading: false,
    error: null
  },
  reducers: {
    clearDepositSettingsState (state) {
      state.error = null
      state.getLoading = false
      state.createLoading = false
      state.updateLoading = false
      state.deleteLoading = false
      state.depositSettings = {}
      state.depositRules = []
      state.singleDepositRule = {}
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchDepositSettings.pending, state => {
        state.getLoading = true
        state.error = null
      })
      .addCase(fetchDepositSettings.fulfilled, (state, action) => {
        state.getLoading = false
        state.depositSettings = action.payload.reservation_deposit_setting
        state.depositRules = action.payload.reservation_deposit_rules
      })
      .addCase(fetchDepositSettings.rejected, (state, action) => {
        state.getLoading = false
        state.error = action.payload || 'Failed to fetch deposit settings'
      })
      .addCase(createNewDepositSetting.pending, state => {
        state.createLoading = true
        state.error = null
      })
      .addCase(createNewDepositSetting.fulfilled, (state, action) => {
        state.createLoading = false
        state.depositRules.push(action.payload)
      })
      .addCase(createNewDepositSetting.rejected, (state, action) => {
        state.createLoading = false
        state.error = action.payload || 'Failed to create deposit setting'
      })
      .addCase(fetchDepositSettingById.pending, state => {
        state.getLoading = true
        state.error = null
      })
      .addCase(fetchDepositSettingById.fulfilled, (state, action) => {
        state.getLoading = false
        state.singleDepositRule = action.payload
      })
      .addCase(fetchDepositSettingById.rejected, (state, action) => {
        state.getLoading = false
        state.error = action.payload || 'Failed to fetch deposit setting'
      })
      .addCase(updateExistingDepositSetting.pending, state => {
        state.updateLoading = true
        state.error = null
      })
      .addCase(updateExistingDepositSetting.fulfilled, (state, action) => {
        state.updateLoading = false
        const index = state.depositRules.findIndex(
          rule => rule.id === action.payload.id
        )
        if (index !== -1) {
          state.depositRules[index] = action.payload
        }
      })
      .addCase(updateExistingDepositSetting.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload || 'Failed to update deposit setting'
      })
      .addCase(toggleDepositSettingStatus.pending, state => {
        state.updateLoading = true
        state.error = null
      })
      .addCase(toggleDepositSettingStatus.fulfilled, (state, action) => {
        state.updateLoading = false
        state.depositSettings = action.payload
      })
      .addCase(toggleDepositSettingStatus.rejected, (state, action) => {
        state.updateLoading = false
        state.error =
          action.payload || 'Failed to toggle deposit setting status'
      })
      .addCase(deleteDepositSettingById.pending, state => {
        state.deleteLoading = true
        state.error = null
      })
      .addCase(deleteDepositSettingById.fulfilled, (state, action) => {
        state.deleteLoading = false
        state.depositRules = state.depositRules.filter(
          rule => rule.id !== action.payload
        )
      })
      .addCase(deleteDepositSettingById.rejected, (state, action) => {
        state.deleteLoading = false
        state.error = action.payload || 'Failed to delete deposit setting'
      })
  }
})

export const { clearDepositSettingsState } = depositSettingsSlice.actions

export default depositSettingsSlice.reducer
